import type { DockviewApi } from "dockview"
import type {
  DockviewGroupPanel,
  FloatingGroupOptions,
  IDockviewComponent,
  Position,
  SerializedFloatingGroup,
} from "dockview-core"

import { log } from "helpers/logger"

import { getDockviewInnerComponent } from "./dockview-api-internals"

export type FloatingPositionSnapshot = SerializedFloatingGroup["position"]

interface FloatingOverlay {
  toJSON: () => FloatingPositionSnapshot
  setBounds: (bounds?: Record<string, unknown>) => void
  options: { container: HTMLElement }
}

/** Remembered floating bounds while a floating group is temporarily docked so maximize can restore it. */
const pendingFloatingPositions = new Map<string, FloatingPositionSnapshot>()

// Sorted panel ids at save time; after merge into a grid group the list is wider — use resolvePendingFloatingSessionKey to look up.
export function getFloatingSessionKeyForGroup(group: { panels: readonly { id: string }[] }): string {
  return group.panels
    .map((panel) => panel.id)
    .sort()
    .join("|")
}

function parseFloatingSessionKey(key: string): string[] {
  return key.split("|").filter(Boolean)
}

function resolvePendingFloatingSessionKey(
  group: { panels: readonly { id: string }[] },
  activePanelId?: string,
): string | undefined {
  const fullKey = getFloatingSessionKeyForGroup(group)
  if (pendingFloatingPositions.has(fullKey)) {
    return fullKey
  }
  const currentIds = new Set(group.panels.map((panel) => panel.id))
  const candidates: string[] = []
  for (const key of pendingFloatingPositions.keys()) {
    const sessionPanelIds = parseFloatingSessionKey(key)
    if (sessionPanelIds.length === 0) continue
    if (!sessionPanelIds.every((id) => currentIds.has(id))) continue
    if (activePanelId != null && !sessionPanelIds.includes(activePanelId)) continue
    candidates.push(key)
  }
  if (candidates.length === 0) {
    return undefined
  }
  if (candidates.length === 1) {
    return candidates[0]
  }
  return undefined
}

/** When > 0, `onDidRemoveGroup` must not drop pending snapshots (moveGroup removes the floating group during dock-for-maximize). */
let pendingFloatingCleanupSuppressionDepth = 0

function getDockviewComponent(api: DockviewApi): IDockviewComponent | null {
  const inner = getDockviewInnerComponent(api)
  return inner != null ? (inner as IDockviewComponent) : null
}

/** Maps a serialized floating snapshot from dockview to options for `addFloatingGroup`. */
export function floatingPositionSnapshotToGroupOptions(box: FloatingPositionSnapshot): FloatingGroupOptions {
  const { width, height } = box
  if ("left" in box && "top" in box) {
    return { width, height, x: box.left, y: box.top }
  }
  if ("left" in box && "bottom" in box) {
    return { width, height, position: { left: box.left, bottom: box.bottom } }
  }
  if ("right" in box && "top" in box) {
    return { width, height, position: { right: box.right, top: box.top } }
  }
  if ("right" in box && "bottom" in box) {
    return { width, height, position: { right: box.right, bottom: box.bottom } }
  }
  return { width, height }
}

export function getFloatingDockMaximizeSession(
  group: DockviewGroupPanel,
  activePanelId?: string,
): FloatingPositionSnapshot | undefined {
  const sessionKey = resolvePendingFloatingSessionKey(group, activePanelId)
  if (!sessionKey) {
    return undefined
  }
  return pendingFloatingPositions.get(sessionKey)
}

/** Clears all pending maximize sessions (e.g. layout unmount or new Dockview instance). Session keys are not stable across instances. */
export function clearPendingFloatingPositions(): void {
  pendingFloatingPositions.clear()
}

/** Clears remembered floating geometry when a group is removed (e.g. last panel closed while maximized). */
export function subscribePendingFloatingPositionCleanup(api: DockviewApi): { dispose: () => void } {
  return api.onDidRemoveGroup((removedGroup) => {
    if (pendingFloatingCleanupSuppressionDepth > 0) {
      return
    }
    const removedIds = new Set(removedGroup.panels.map((panel) => panel.id))
    for (const key of [...pendingFloatingPositions.keys()]) {
      const sessionPanelIds = parseFloatingSessionKey(key)
      if (sessionPanelIds.length === 0) continue
      if (sessionPanelIds.every((id) => removedIds.has(id))) {
        pendingFloatingPositions.delete(key)
      }
    }
  })
}

/** First docked grid group (tab strip) in layout order, excluding the given id (e.g. floating group). */
function findFirstGridGroup(api: DockviewApi, excludeGroupId: string): DockviewGroupPanel | undefined {
  return api.groups.find(
    (candidate) => candidate.api.id !== excludeGroupId && candidate.api.location.type === "grid",
  )
}

export function getFloatingOverlayForGroup(
  group: DockviewGroupPanel,
  api: DockviewApi,
): FloatingOverlay | null {
  const component = getDockviewComponent(api)
  if (!component) {
    return null
  }

  const withFloating = component as IDockviewComponent & {
    floatingGroups: { group: DockviewGroupPanel, overlay: FloatingOverlay }[]
  }

  const groupId = group.api.id
  return withFloating.floatingGroups.find((entry) => entry.group.api.id === groupId)?.overlay ?? null
}

/** Split direction when docking for maximize — own group adjacent to the main grid, not merged into its tabs. */
const FLOATING_MAXIMIZE_DOCK_POSITION: Position = "right"

/**
 * Moves a floating group into the main docked grid (e.g. Dock control).
 * Use `position` `"center"` to merge into the target group's tab strip; use a side (`"right"`, `"left"`, etc.) for an adjacent split.
 */
export function dockFloatingGroupToGrid(
  group: DockviewGroupPanel,
  api: DockviewApi,
  position: Position = "center",
): boolean {
  const component = getDockviewComponent(api)
  const targetGridGroup = findFirstGridGroup(api, group.api.id)
  if (!component || !targetGridGroup) {
    return false
  }

  try {
    component.moveGroup({
      from: { group },
      to: { group: targetGridGroup, position },
    })
    return true
  } catch (error) {
    log(error)
    return false
  }
}

/**
 * Saves floating geometry, then docks as an adjacent split (not merged into the first group's tabs) so the group can be maximized on its own.
 */
export function dockFloatingGroupForMaximize(group: DockviewGroupPanel, api: DockviewApi): boolean {
  const overlay = getFloatingOverlayForGroup(group, api)
  if (!overlay) {
    return false
  }

  const sessionKey = getFloatingSessionKeyForGroup(group)
  pendingFloatingPositions.set(sessionKey, overlay.toJSON())

  pendingFloatingCleanupSuppressionDepth += 1
  try {
    if (!dockFloatingGroupToGrid(group, api, FLOATING_MAXIMIZE_DOCK_POSITION)) {
      pendingFloatingPositions.delete(sessionKey)
      return false
    }
  } finally {
    pendingFloatingCleanupSuppressionDepth -= 1
  }

  return true
}

export function restoreFloatingGroupAfterMaximizedDock(
  group: DockviewGroupPanel,
  api: DockviewApi,
  activePanelId?: string,
): boolean {
  const sessionKey = resolvePendingFloatingSessionKey(group, activePanelId)
  if (!sessionKey) {
    return false
  }
  const position = pendingFloatingPositions.get(sessionKey)
  if (!position) {
    return false
  }

  try {
    api.addFloatingGroup(group, floatingPositionSnapshotToGroupOptions(position))
    pendingFloatingPositions.delete(sessionKey)
    return true
  } catch (error) {
    log(error)
    pendingFloatingPositions.delete(sessionKey)
    return false
  }
}
